import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import ReflectionPage from './pages/ReflectionPage';
import AnalyticsPage from './pages/AnalyticsPage';
import WorkspacePage from './pages/WorkspacePage';
import InvitePage from './pages/InvitePage';
import VerifyEmail from './pages/VerifyEmail';
import AboutPage from './pages/AboutPage';
import CheckoutPage from './pages/CheckoutPage';
import MockCheckoutPage from './pages/MockCheckoutPage';
import PaymentMethodPage from './pages/PaymentMethodPage';
import StripeCheckoutPage from './pages/StripeCheckoutPage';
import RazorpayCheckoutPage from './pages/RazorpayCheckoutPage';
import WelcomePage from './pages/WelcomePage';
import { Toaster } from '@/components/ui/sonner';

function App() {
  const [token, setToken] = useState(localStorage.getItem('align_token'));
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (token) {
      localStorage.setItem('align_token', token);
    } else {
      localStorage.removeItem('align_token');
    }
  }, [token]);

  // After a refresh (or first load with a stored token), rehydrate the user
  // so pages that need the current user's id/email have it without each page
  // re-fetching independently.
  useEffect(() => {
    if (!token || user) return;
    let cancelled = false;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios
      .get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) setUser(data); })
      .catch((err) => {
        // Token rejected — clear it so the user gets bounced to the landing page.
        if (!cancelled && err.response?.status === 401) setToken(null);
      });
    return () => { cancelled = true; };
  }, [token, user]);

  const ProtectedRoute = ({ children, bypassSubscriptionCheck = false, bypassWelcomeCheck = false }) => {
    if (!token) return <Navigate to="/" />;
    
    if (!user) {
      return (
        <div className="min-h-screen w-screen flex flex-col items-center justify-center bg-[#FDFCF8] dark:bg-[#121212]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          <p className="mt-3 text-xs text-muted-foreground">Loading workspace...</p>
        </div>
      );
    }

    if (!bypassWelcomeCheck && user.subscription?.status === 'trialing' && !user.welcome_seen) {
      return <Navigate to="/welcome" />;
    }

    if (!bypassSubscriptionCheck && user.subscription?.status === 'unpaid') {
      return <Navigate to="/checkout" />;
    }

    return children;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage setToken={setToken} setUser={setUser} />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard token={token} user={user} setToken={setToken} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reflect"
            element={
              <ProtectedRoute>
                <ReflectionPage token={token} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage token={token} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/corporate"
            element={
              <ProtectedRoute>
                <WorkspacePage mode="corporate" token={token} user={user} setToken={setToken} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/family"
            element={
              <ProtectedRoute>
                <WorkspacePage mode="family" token={token} user={user} setToken={setToken} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/welcome"
            element={
              <ProtectedRoute bypassSubscriptionCheck={true} bypassWelcomeCheck={true}>
                <WelcomePage user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute bypassSubscriptionCheck={true}>
                {user?.subscription?.status === 'active' ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <CheckoutPage />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/mock"
            element={
              <ProtectedRoute bypassSubscriptionCheck={true}>
                {user?.subscription?.status === 'active' ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <MockCheckoutPage token={token} setUser={setUser} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/payment-method"
            element={
              <ProtectedRoute bypassSubscriptionCheck={true}>
                {user?.subscription?.status === 'active' ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <PaymentMethodPage />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/stripe"
            element={
              <ProtectedRoute bypassSubscriptionCheck={true}>
                {user?.subscription?.status === 'active' ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <StripeCheckoutPage token={token} setUser={setUser} />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/razorpay"
            element={
              <ProtectedRoute bypassSubscriptionCheck={true}>
                {user?.subscription?.status === 'active' ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <RazorpayCheckoutPage token={token} setUser={setUser} />
                )}
              </ProtectedRoute>
            }
          />
          {/* /invite/:token is intentionally unprotected — anyone with the link can preview;
              the page itself handles the "sign in first" flow. */}
          <Route path="/invite/:token" element={<InvitePage token={token} setToken={setToken} />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/about" element={<AboutPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
